import React, { useState, useEffect, useMemo } from "react";
import styles from "./LevelsTab.module.scss";
import {
  MdBlock,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdSettings,
  MdAdd,
  MdSave,
} from "react-icons/md";
import Axios from "../../../../_services/caller.service";
import { toast } from "react-toastify";

const PROTECTED_LEVELS = ["support1", "support2"];

const LevelsTab = ({ role }) => {
  const [levels, setLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState("");
  const [permissionsData, setPermissionsData] = useState([]);
  const [newLevelName, setNewLevelName] = useState("");
  const [renameLevelName, setRenameLevelName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Chargement initial des modules/actions + niveaux
  useEffect(() => {
    (async () => {
      try {
        const [modsRes, lvlsRes] = await Promise.all([
          Axios.get(`/getAllModulesWithActions?role=${role}`),
          Axios.get(`/getAllLevelsWithAccess?role=${role}`),
        ]);
        setPermissionsData(modsRes.data);
        setLevels(lvlsRes.data);
        if (lvlsRes.data.length) {
          setSelectedLevelId(lvlsRes.data[0].id);
          setRenameLevelName(lvlsRes.data[0].name);
        }
      } catch (err) {
        console.error(err);
        toast.error("Erreur de chargement");
      }
    })();
  }, [role]);

  const selectedLevel = useMemo(
    () => levels.find((l) => l.id === selectedLevelId),
    [levels, selectedLevelId]
  );

  // Lorsque l'on change de niveau sélectionné, mettre à jour le champ de renommage
  useEffect(() => {
    if (selectedLevel) {
      setRenameLevelName(selectedLevel.name);
    }
  }, [selectedLevel]);

  // ⚡️ mise à jour immédiate au toggle de permission
  const updatePermission = async (actionKey, value) => {
    if (!selectedLevel) return;

    // 1) Mettre à jour le state local
    const updatedLevels = levels.map((lvl) =>
      lvl.id === selectedLevelId
        ? {
            ...lvl,
            permissions: { ...lvl.permissions, [actionKey]: value },
          }
        : lvl
    );
    setLevels(updatedLevels);

    // 2) Enregistrer au back
    try {
      const payload = updatedLevels.find((l) => l.id === selectedLevelId);
      await Axios.post(`/saveSupportLevelActions`, payload);
      toast.success("✅ Permission mise à jour");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // ─── Création d'un nouveau niveau ───
  const handleAddLevel = async () => {
    if (!newLevelName.trim()) {
      toast.error("Le nom ne peut pas être vide");
      return;
    }
    try {
      const res = await Axios.post(
        `/createSupportLevelMeta?role=${encodeURIComponent(role)}`,
        { name: newLevelName.trim() }
      );
      // Le backend renvoie { id, name, ... }
      const created = {
        id: res.data.id,
        name: res.data.name,
        role,
        // On part sur la base : permissions à 0 pour chaque action
        permissions: permissionsData.reduce((acc, mod) => {
          mod.actions.forEach((a) => {
            acc[a.key] = 0;
          });
          return acc;
        }, {}),
      };
      setLevels((prev) => [...prev, created]);
      setSelectedLevelId(created.id);
      setNewLevelName("");
      toast.success("✅ Nouveau niveau ajouté");
    } catch (err) {
      console.error("Erreur création niveau :", err);
      toast.error("Échec de la création du niveau");
    }
  };

  // ─── Renommage du niveau sélectionné ───
  const handleRenameLevel = async () => {
    if (!selectedLevel) return;
    if (!renameLevelName.trim()) {
      toast.error("Le nouveau nom ne peut pas être vide");
      return;
    }
    try {
      await Axios.put(
        `/renameSupportLevel/${selectedLevelId}`,
        { name: renameLevelName.trim() }
      );
      setLevels((prev) =>
        prev.map((lvl) =>
          lvl.id === selectedLevelId
            ? { ...lvl, name: renameLevelName.trim() }
            : lvl
        )
      );
      toast.success("✅ Niveau renommé avec succès");
    } catch (err) {
      console.error("Erreur renommage niveau :", err);
      toast.error("Échec du renommage");
    }
  };

  // ─── Suppression du niveau sélectionné ───
  const handleDeleteLevel = async () => {
    if (!selectedLevel) return;
    if (PROTECTED_LEVELS.includes(selectedLevel.name)) {
      toast.error("Ce niveau est protégé et ne peut pas être supprimé");
      return;
    }
    try {
      await Axios.delete(`/deleteSupportLevel/${selectedLevelId}`);
      setLevels((prev) => prev.filter((lvl) => lvl.id !== selectedLevelId));
      // Réinitialiser la sélection sur le premier niveau disponible
      setTimeout(() => {
        setSelectedLevelId((prev) => {
          const remaining = levels.filter((lvl) => lvl.id !== prev);
          if (remaining.length) {
            return remaining[0].id;
          }
          return "";
        });
      }, 0);
      toast.success("✅ Niveau supprimé");
    } catch (err) {
      console.error("Erreur suppression niveau :", err);
      toast.error("Échec de la suppression");
    }
  };

  if (!selectedLevel) {
    return (
      <div className={styles.emptyState}>
        <p>⚠️ Aucun niveau trouvé.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Sélecteur de niveau */}
      <div className={styles.selectRow}>
        <label>📁 Niveau :</label>
        <select
          value={selectedLevelId}
          onChange={(e) => setSelectedLevelId(Number(e.target.value))}
        >
          {levels.map((lvl) => (
            <option key={lvl.id} value={lvl.id}>
              {lvl.name}
            </option>
          ))}
        </select>

        {/* Bouton pour afficher/cacher les paramètres avancés */}
        <button
          className={styles.advancedToggle}
          onClick={() => setShowAdvanced((v) => !v)}
        >
          <MdSettings /> Paramètres avancés
        </button>
      </div>

      {/* Section “Avancé” : ajout / renommage / suppression */}
      {showAdvanced && (
        <div className={styles.advancedSection}>
          {/* Création d’un nouveau niveau */}
          <div className={styles.advancedRow}>
            <input
              type="text"
              placeholder="Nouveau nom de niveau"
              value={newLevelName}
              onChange={(e) => setNewLevelName(e.target.value)}
            />
            <button onClick={handleAddLevel} className={styles.addBtn}>
              <MdAdd /> Ajouter
            </button>
          </div>

          {/* Renommage du niveau actuel */}
          <div className={styles.advancedRow}>
            <input
              type="text"
              placeholder="Renommer le niveau"
              value={renameLevelName}
              onChange={(e) => setRenameLevelName(e.target.value)}
            />
            <button onClick={handleRenameLevel} className={styles.saveBtn}>
              <MdSave /> Renommer
            </button>
          </div>

          {/* Suppression du niveau actuel */}
          <div className={styles.advancedRow}>
            <button
              onClick={handleDeleteLevel}
              className={styles.deleteBtn}
              disabled={PROTECTED_LEVELS.includes(selectedLevel.name)}
            >
              <MdDelete /> Supprimer le niveau
            </button>
          </div>
        </div>
      )}

      {/* Légende des icônes */}
      <div className={styles.legend}>
        <ul className={styles.legendList}>
          <li>
            <MdBlock className={styles.badgeNone} /> Aucun
          </li>
          <li>
            <MdEdit className={styles.badgeFull} /> Lecture+Écriture
          </li>
          <li>
            <MdVisibility className={styles.badgeRead} /> Lecture
          </li>
        </ul>
      </div>

      {/* Tableau des permissions du niveau sélectionné */}
    {permissionsData.map((mod) => (
  <div key={mod.id} className={styles.moduleCard}>
    <h3 className={styles.moduleTitle}>{mod.name}</h3>
    <table className={styles.permissionsTable}>
      <thead>
        <tr>
          <th>Action</th>
          <th>Description</th>
          <th>Permissions</th>
          {[0, 2, 1].map((val) => (
            <th key={val}>
              {val === 0 && <MdBlock className={styles.badgeNone} />}
              {val === 2 && <MdEdit className={styles.badgeFull} />}
              {val === 1 && <MdVisibility className={styles.badgeRead} />}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {mod.actions.map((action) => {
          const current = selectedLevel.permissions[action.key] ?? 0;
          return (
            <tr key={action.key}>
              <td>{action.key}</td>
              <td>{action.description || "-"}</td>
              <td>
                {current === 0 && <MdBlock className={styles.badgeNone} />}
                {current === 2 && <MdEdit className={styles.badgeFull} />}
                {current === 1 && (
                  <MdVisibility className={styles.badgeRead} />
                )}
              </td>
              {[0, 2, 1].map((val) => (
                <td key={val}>
                  <input
                    type="checkbox"
                    checked={current === val}
                    onChange={() => updatePermission(action.key, val)}
                  />
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
))}

    </div>
  );
};

export default LevelsTab;
